import { Avatar, AvatarFallback, AvatarImage } from '@monksflow/monks-ui';
import clsx from 'clsx';
import type { ReactNode } from 'react';

import { TruncatedText } from '@/shared/ui/TruncatedText';
import { getInitials } from '@/shared/utils/getInitials';
import { useTranslation } from 'react-i18next';

export interface PreResolvedAvatarCellProps {
  /** Resolved display name. `null` renders the muted em-dash fallback. */
  name: string | null;
  /** Resolved avatar image URL. `null` falls back to initials only. */
  avatarUrl: string | null;
  /** Explicit override for the avatar fallback initials. Defaults to `getInitials(name)`. */
  initials?: string;
  /**
   * Replaces the visible name (e.g. ProjectsTable's `"Me"` mapping for the
   * current user). Initials still derive from `name`, so the avatar
   * fallback stays personal even when the label is overridden.
   */
  displayNameOverride?: ReactNode;
  /**
   * Wraps the label in `<TruncatedText>` (string overrides) OR in a
   * `truncate` span (JSX overrides), so truncation is preserved instead
   * of silently swapping back to the raw name.
   */
  truncate?: boolean;
  className?: string;
  /** Tessra avatar size step. Defaults to `mini` (24px) — the shape every existing consumer relies on. */
  size?: 'mini' | 'sm';
}

/**
 * Pure-presentational avatar + name cell. Domain-agnostic: NO
 * `@/entities/user` import. Callers with pre-resolved `{name, avatarUrl}`
 * import this directly. A variant that resolves a userId itself is not
 * shipped; one would belong under `entities/user/ui/` (FSD-legal home) and
 * could compose this, since the shape is the same. `UserAvatar` there is not
 * that variant: it is the avatar alone, and this cell always renders a label
 * region, down to the em-dash when there is no name.
 *
 * Null/empty `name` renders the canonical em-dash fallback in
 * `text-muted-foreground`. Whitespace-only names are treated as null.
 */
export const PreResolvedAvatarCell = ({
  name,
  avatarUrl,
  initials,
  displayNameOverride,
  truncate,
  className,
  size = 'mini',
}: PreResolvedAvatarCellProps) => {
  const { t } = useTranslation('common');

  // Normalize once. A whitespace-only name ("   ") should behave
  // IDENTICALLY to `null` — em-dash fallback + no initials (otherwise
  // `getInitials("   ")` produces garbage AND the label branch renders a
  // blank span with no visual signal).
  const trimmedName = name?.trim() ? name.trim() : null;
  const resolvedInitials = initials ?? (trimmedName ? getInitials(trimmedName) : '');
  const display = displayNameOverride ?? trimmedName;
  const isStringDisplay = typeof display === 'string';

  return (
    <div className={clsx('flex min-w-0 items-center gap-2', className)}>
      <Avatar size={size} roundness="full">
        {avatarUrl ? (
          // When the name is null (or normalized away) but an avatar URL
          // IS present, fall back to a generic label so SRs still announce
          // the image meaningfully instead of skipping it as decorative
          // (alt="").
          <AvatarImage
            src={avatarUrl}
            alt={trimmedName ?? t('components.preResolvedAvatarCell.userAvatar')}
          />
        ) : null}
        <AvatarFallback size={size} roundness="full">
          {resolvedInitials}
        </AvatarFallback>
      </Avatar>
      {trimmedName ? (
        truncate && isStringDisplay ? (
          <TruncatedText value={display} className="text-sm text-foreground" />
        ) : truncate ? (
          // JSX override with truncate: wrap in a truncating span so the
          // intent is preserved. Consumers can still lose truncation if
          // the JSX brings its own layout — documented tradeoff.
          <span className="truncate text-sm text-foreground">{display}</span>
        ) : (
          <span className="text-sm text-foreground">{display}</span>
        )
      ) : (
        <span className="text-sm text-muted-foreground">—</span>
      )}
    </div>
  );
};
PreResolvedAvatarCell.displayName = 'PreResolvedAvatarCell';
